/* Spaghetti / Flow Diagram — distance & motion waste mapping
 * Click on the canvas to drop waypoints; lines auto-connect and total
 * distance is computed. Multiple "paths" (e.g. operator A vs B) supported.
 */
import { useRef, useState } from "react";
import ToolWizard from "./ToolWizard";
import { Plus, Trash2, FileText, MapPin, Eraser } from "lucide-react";

const PATH_COLORS = ["#7C9A7E", "#D97706", "#6366F1", "#DC2626", "#0EA5E9", "#A855F7"];

function dist(p, q) {
  const dx = q.x - p.x, dy = q.y - p.y;
  return Math.sqrt(dx * dx + dy * dy);
}

function pathTotalDistance(points, scale) {
  let d = 0;
  for (let i = 1; i < points.length; i++) d += dist(points[i - 1], points[i]);
  return d * (scale || 1);
}

function SpaghettiCanvas({ paths, activePathId, scale, addPoint, removePoint, label }) {
  const ref = useRef(null);
  const onClick = (e) => {
    if (!activePathId) return;
    const rect = ref.current.getBoundingClientRect();
    const x = ((e.clientX - rect.left) / rect.width) * 1000;
    const y = ((e.clientY - rect.top) / rect.height) * 600;
    addPoint(activePathId, { x, y, label: "" });
  };
  return (
    <div className="border-2 border-sage-200 rounded-md bg-white relative" style={{ aspectRatio: "1000/600" }}>
      <svg ref={ref} viewBox="0 0 1000 600" onClick={onClick} className="w-full h-full cursor-crosshair" data-testid="sp-canvas">
        <defs>
          <pattern id="grid" width="50" height="50" patternUnits="userSpaceOnUse">
            <path d="M 50 0 L 0 0 0 50" fill="none" stroke="#E2E8E3" strokeWidth="0.5" />
          </pattern>
        </defs>
        <rect width="1000" height="600" fill="url(#grid)" />
        {paths.map((p, pIdx) => {
          const color = p.color || PATH_COLORS[pIdx % PATH_COLORS.length];
          const pts = p.points || [];
          if (pts.length < 1) return null;
          const polylinePts = pts.map(pt => `${pt.x},${pt.y}`).join(" ");
          return (
            <g key={p.id} opacity={p.id === activePathId ? 1 : 0.5}>
              {pts.length >= 2 && <polyline points={polylinePts} fill="none" stroke={color} strokeWidth="2.5" strokeDasharray="4,3" />}
              {pts.map((pt, i) => (
                <g key={i} onClick={(e) => { e.stopPropagation(); removePoint(p.id, i); }} style={{ cursor: "pointer" }}>
                  <circle cx={pt.x} cy={pt.y} r="9" fill={color} stroke="white" strokeWidth="2" />
                  <text x={pt.x} y={pt.y + 3.5} textAnchor="middle" fontSize="10" fill="white" fontWeight="700">{i + 1}</text>
                </g>
              ))}
            </g>
          );
        })}
      </svg>
      {label && <div className="absolute top-2 left-2 bg-white/90 rounded px-2 py-0.5 text-xs font-semibold text-sage-700">{label}</div>}
    </div>
  );
}

export default function SpaghettiWizard({ project, onSave, onExportPDF }) {
  const steps = [
    {
      key: "setup", label: "Setup",
      render: ({ data, updateData }) => (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3" data-testid="sp-setup">
          <div className="md:col-span-2"><label className="block text-xs font-semibold text-sage-600 mb-1">Layout / area</label><input value={data.area || ""} onChange={e => updateData({ area: e.target.value })} className="w-full border border-sage-200 rounded-md px-3 py-2 text-sm" placeholder="e.g. Warehouse — Floor 2" /></div>
          <div><label className="block text-xs font-semibold text-sage-600 mb-1">Scale (m per 100 grid units)</label><input data-testid="sp-scale" type="number" step="0.1" value={data.scale || 1} onChange={e => updateData({ scale: Number(e.target.value) })} className="w-full border border-sage-200 rounded-md px-3 py-2 text-sm" /></div>
          <div><label className="block text-xs font-semibold text-sage-600 mb-1">Cycles observed / day</label><input type="number" value={data.cycles_per_day || 1} onChange={e => updateData({ cycles_per_day: Number(e.target.value) })} className="w-full border border-sage-200 rounded-md px-3 py-2 text-sm" /></div>
          <div className="md:col-span-2"><label className="block text-xs font-semibold text-sage-600 mb-1">Goal of the walk</label><textarea value={data.goal || ""} onChange={e => updateData({ goal: e.target.value })} className="w-full border border-sage-200 rounded-md px-3 py-2 text-sm min-h-[60px]" /></div>
        </div>
      ),
    },
    {
      key: "draw", label: "Plot Path",
      render: ({ data, updateData }) => {
        const paths = data.paths || [];
        const activeId = data.active_path_id || (paths[0]?.id);
        const setActive = (id) => updateData({ active_path_id: id });
        const addPath = () => {
          const id = `p-${Date.now()}`;
          const newPaths = [...paths, { id, label: `Path ${paths.length + 1}`, color: PATH_COLORS[paths.length % PATH_COLORS.length], points: [] }];
          updateData({ paths: newPaths, active_path_id: id });
        };
        const updPath = (id, patch) => updateData({ paths: paths.map(p => p.id === id ? { ...p, ...patch } : p) });
        const rmPath  = (id) => updateData({ paths: paths.filter(p => p.id !== id) });
        const addPoint = (id, pt) => updPath(id, { points: [...(paths.find(p => p.id === id)?.points || []), pt] });
        const removePoint = (id, idx) => updPath(id, { points: (paths.find(p => p.id === id)?.points || []).filter((_, i) => i !== idx) });
        const clearPath = (id) => updPath(id, { points: [] });
        return (
          <div className="space-y-3" data-testid="sp-draw">
            <div className="bg-sage-50 rounded-md p-2 text-xs text-sage-600">Click on the grid to drop waypoints. Click an existing waypoint to remove it. Toggle paths to compare operators / scenarios.</div>
            <div className="flex flex-wrap gap-2 items-center" data-testid="sp-paths">
              {paths.map(p => (
                <div key={p.id} className={`flex items-center gap-1 border-2 rounded-md px-2 py-1 ${p.id === activeId ? "shadow-md" : "opacity-60"}`} style={{ borderColor: p.color }}>
                  <input type="color" value={p.color} onChange={e => updPath(p.id, { color: e.target.value })} className="w-5 h-5 rounded cursor-pointer border-0 p-0" />
                  <input value={p.label} onChange={e => updPath(p.id, { label: e.target.value })} className="bg-transparent w-32 outline-none text-xs text-sage-800" />
                  <button data-testid={`sp-path-activate-${p.id}`} onClick={() => setActive(p.id)} className={`text-xs px-2 py-0.5 rounded ${p.id === activeId ? "bg-sage-700 text-white" : "bg-white text-sage-700 border border-sage-200 hover:bg-sage-50"}`}>{p.id === activeId ? "Active" : "Activate"}</button>
                  <button onClick={() => clearPath(p.id)} title="Clear points" className="text-sage-500 hover:text-amber-600"><Eraser className="w-3.5 h-3.5" /></button>
                  {paths.length > 1 && <button onClick={() => rmPath(p.id)} className="text-red-400 hover:text-red-600"><Trash2 className="w-3.5 h-3.5" /></button>}
                </div>
              ))}
              <button data-testid="sp-add-path" onClick={addPath} className="flex items-center gap-1 border border-dashed border-sage-300 text-sage-600 rounded-md px-2 py-1 text-xs hover:bg-sage-50"><Plus className="w-3 h-3" /> Add path</button>
            </div>
            <SpaghettiCanvas paths={paths} activePathId={activeId} scale={data.scale} addPoint={addPoint} removePoint={removePoint} label={`${data.area || ""} · scale ${(data.scale || 1)} m/100u`} />
            <div className="grid grid-cols-1 md:grid-cols-3 gap-2 text-xs">
              {paths.map(p => (
                <div key={p.id} className="bg-white border-l-4 rounded-md p-2" style={{ borderLeftColor: p.color }}>
                  <p className="font-semibold text-sage-900">{p.label}</p>
                  <p className="text-sage-600">{(p.points || []).length} waypoints · {pathTotalDistance(p.points || [], data.scale || 1).toFixed(1)} m</p>
                </div>
              ))}
            </div>
          </div>
        );
      },
    },
    {
      key: "diagnostic", label: "Diagnostic",
      render: ({ data }) => {
        const paths = data.paths || [];
        const scale = data.scale || 1;
        const cycles = data.cycles_per_day || 1;
        const totalsPerPath = paths.map(p => ({ ...p, total: pathTotalDistance(p.points || [], scale), points: p.points || [] }));
        const grandTotalPerCycle = totalsPerPath.reduce((a, p) => a + p.total, 0);
        const dayTotal = grandTotalPerCycle * cycles;
        return (
          <div className="space-y-4" data-testid="sp-diagnostic">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              <div className="bg-sage-50 rounded-md p-3"><p className="text-xs text-sage-500 uppercase tracking-wider font-semibold">Paths</p><p className="font-heading text-2xl font-bold text-sage-900">{paths.length}</p></div>
              <div className="bg-sage-50 rounded-md p-3"><p className="text-xs text-sage-500 uppercase tracking-wider font-semibold">Distance / cycle</p><p className="font-heading text-2xl font-bold text-sage-900">{grandTotalPerCycle.toFixed(1)} <span className="text-sm font-normal text-sage-500">m</span></p></div>
              <div className="bg-amber-50 rounded-md p-3 border border-amber-200"><p className="text-xs uppercase tracking-wider font-semibold text-amber-800">Distance / day</p><p className="font-heading text-2xl font-bold text-amber-900">{dayTotal.toFixed(0)} <span className="text-sm font-normal">m</span></p></div>
              <div className="bg-sage-50 rounded-md p-3"><p className="text-xs text-sage-500 uppercase tracking-wider font-semibold">Cycles / day</p><p className="font-heading text-2xl font-bold text-sage-900">{cycles}</p></div>
            </div>
            <SpaghettiCanvas paths={paths} activePathId={null} scale={scale} addPoint={() => {}} removePoint={() => {}} label="Read-only diagnostic view" />
            <table className="w-full text-xs border border-sage-100 rounded-md overflow-hidden">
              <thead className="bg-sage-50 text-sage-600 uppercase tracking-wider"><tr><th className="text-left px-2 py-1.5">Path</th><th className="text-right px-2 py-1.5">Waypoints</th><th className="text-right px-2 py-1.5">Per cycle (m)</th><th className="text-right px-2 py-1.5">Per day (m)</th></tr></thead>
              <tbody>{totalsPerPath.map(p => (
                <tr key={p.id} className="border-t border-sage-100">
                  <td className="px-2 py-1 font-semibold" style={{ color: p.color }}>{p.label}</td>
                  <td className="text-right px-2 py-1 font-mono">{p.points.length}</td>
                  <td className="text-right px-2 py-1 font-mono">{p.total.toFixed(1)}</td>
                  <td className="text-right px-2 py-1 font-mono font-bold">{(p.total * cycles).toFixed(0)}</td>
                </tr>
              ))}</tbody>
            </table>
            <button data-testid="export-spaghetti-pdf" onClick={() => onExportPDF(data)} className="bg-sage-600 text-white rounded-md px-4 py-2 text-sm font-semibold hover:bg-sage-700 flex items-center gap-2"><FileText className="w-4 h-4" /> Export PDF</button>
          </div>
        );
      },
    },
  ];
  return <ToolWizard project={project} steps={steps} onSave={onSave} completedLabel="Finish Spaghetti" />;
}

export { pathTotalDistance };
